import useGetStowagePlan from 'src/api/stowage/useGetStowageApi';
import { useRotationStore } from 'src/context/RotationContext';
import { useStowageStore } from 'src/context/StowageContext';
import { useVesselStore } from 'src/context/VesselContext';
import { useViolationsStore } from 'src/context/ViolationsContext';
import { cleanStowagePlanData } from 'src/factory/Stowage/StowagePlanFactory';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';
import { getStowageDataItems } from 'src/utils/stowage/stowagePlanChangeUtils/stowageDataItemsBuilder';
import { QueryParamProps } from 'odfjell-query-manager';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import useUpdateStowagePlan from '../useUpdateStowagePlan';
import { useAppStore } from 'src/context/AppContext';
import StowagePlanNotFoundMessage from 'src/components/stowage/common/StowagePlanNotFoundMessage';
import React from 'react';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import { useSelectCargoOperation } from '../useSelectCargoOperation';
import { getMasterStowagePlan } from 'src/utils/stowage/stowagePlanUtils';

export const useStowageUrlChangeHandler = () => {
    const { setSelectedCargoOperation, selectedCargoOperation } = useStowageSelectionStore();
    const { stowagePlans, setActiveStowagePlan } = useStowageStore();
    const { setSavedViolations } = useViolationsStore();
    const {
        setStowagePlanRotationPortCallList,
        setStowagePlanRotationCargoList,
        setSelectedStowageRotationBerthCall,
        setSelectedStowageRotationPortCall,
        selectedStowageRotationPortCall,
        selectedStowageRotationBerthCall,
    } = useRotationStore();
    const { getStowagePlanChanges, getStowagePlans } = useGetStowagePlan();
    const { selectedVessel } = useVesselStore();
    const {
        setQueryParamStowagePlan,
        clearQueryParamsStowagePlan,
        clearQueryParamsStowagePortCall,
        clearQueryParamsStowageBerthCall,
        clearQueryParamsStowageCargoOperation,
    } = useStowageQueryParamsUtils();
    const { updateStowageData } = useUpdateStowagePlan();
    const { selectCargoOperation } = useSelectCargoOperation();
    const { setAppModalData } = useAppStore();

    const handleStowageCallQueryParamChanges = (queryParamUpdate: QueryParamProps, filteredStowageRotationTree: RotationPortCall[]) => {
        const getQueryParamValue = (name: string) => queryParamUpdate.queryParamValues.find((qp) => qp.name === name)?.newValue;

        const portCallGuid = getQueryParamValue('portCallGuid');
        const berthCallGuid = getQueryParamValue('berthCallGuid');
        const cargoOperationGuid = getQueryParamValue('cargoOperationGuid');

        if (portCallGuid) {
            const portCallItem = filteredStowageRotationTree.find((pc) => pc.portCallGuid === portCallGuid);

            if (!portCallItem) return clearQueryParamsStowagePortCall();

            if (!selectedStowageRotationPortCall || selectedStowageRotationPortCall.portCallGuid !== portCallGuid) {
                setSelectedStowageRotationPortCall({ ...portCallItem });
                if (!berthCallGuid) setSelectedStowageRotationBerthCall(null);
                if (!cargoOperationGuid) setSelectedCargoOperation(null);
            }

            const berthCallItem = portCallItem.berthCalls.find((bc) => bc.berthCallGuid === berthCallGuid);
            const cargoOperation = portCallItem.berthCalls
                .flatMap((bc) => bc.cargoOperations)
                .find((co) => co.cargoOperationGuid === cargoOperationGuid);
            berthCallGuid ? (berthCallItem ? setSelectedStowageRotationBerthCall({ ...berthCallItem }) : clearQueryParamsStowageBerthCall()) : null;
            cargoOperationGuid ? (cargoOperation ? selectCargoOperation(cargoOperation) : clearQueryParamsStowageCargoOperation()) : null;
        } else {
            if (selectedStowageRotationPortCall) setSelectedStowageRotationPortCall(null);
            if (selectedStowageRotationBerthCall) setSelectedStowageRotationBerthCall(null);
            if (selectedCargoOperation) setSelectedCargoOperation(null);
        }

        if (!berthCallGuid && selectedStowageRotationBerthCall) {
            setSelectedStowageRotationBerthCall(null);
        }
        if (!cargoOperationGuid && selectedCargoOperation) {
            setSelectedCargoOperation(null);
        }
    };

    const handleStowagePlanGuidChange = async (stowagePlanGuid: string) => {
        if (!stowagePlans) return;
        let stowagePlan = stowagePlans.find((sp) => sp.stowagePlanGuid === stowagePlanGuid && !sp.isDeleted);
        setSelectedStowageRotationBerthCall(null);
        setSelectedStowageRotationPortCall(null);
        // Don't reset voyage selection when switching between stowage plans
        // The voyage selection should persist across stowage plan changes
        // setSelectedStowageVoyages(null);
        if (stowagePlan) {
            if (!stowagePlan.isNewStowagePlan) {
                // Force cache refresh to ensure we get the latest changes from the backend
                // This prevents stale data from being shown after F5 refresh
                const _stowagePlanChanges = await getStowagePlanChanges(stowagePlan.stowagePlanGuid, true);
                stowagePlan = cleanStowagePlanData(stowagePlan);
                const { updatedStowagePlan } = await setStowageDataInStowagePlan(_stowagePlanChanges, stowagePlan);
                stowagePlan = updatedStowagePlan;
            }
            setActiveStowagePlan({ ...stowagePlan });
        } else if (stowagePlans.filter((sp) => !sp.isDeleted).length === 0) {
            // If there are no plans available, clear the stowage plan GUID from URL and try to fetch plans
            clearQueryParamsStowagePlan();
            await getStowagePlans(selectedVessel.imoNumber);
        } else {
            setAppModalData({
                title: 'Stowage plan not found',
                message: React.createElement(StowagePlanNotFoundMessage, { vesselName: selectedVessel.vesselName }),
                actionButtonText: 'OK',
                isCloseDisabled: true,
                action: () => {
                    const availablePlans = stowagePlans.filter((sp) => !sp.isDeleted);
                    const nextPlanGuid = getMasterStowagePlan(availablePlans)?.stowagePlanGuid || availablePlans[0]?.stowagePlanGuid;
                    if (nextPlanGuid) {
                        setQueryParamStowagePlan(nextPlanGuid);
                    } else {
                        // Only clear if there are absolutely no plans available
                        clearQueryParamsStowagePlan();
                    }
                    setAppModalData(null);
                },
            });
        }
    };

    const setStowageDataInStowagePlan = async (
        stowagePlanChanges: StowagePlanChange[],
        stowagePlan: StowagePlan,
        updatedStowagePlans?: StowagePlan[]
    ) => {
        if (!selectedVessel) return;
        const _stowagePlans = updatedStowagePlans ?? stowagePlans;
        const { stowagePlanRotationPortCalls, violations, updatedStowagePlan } = getStowageDataItems(stowagePlanChanges, _stowagePlans, stowagePlan);

        setStowagePlanRotationPortCallList([...stowagePlanRotationPortCalls]);
        setStowagePlanRotationCargoList([...stowagePlan.cargoes]);

        setSavedViolations([...violations]);

        await updateStowageData(updatedStowagePlan, _stowagePlans, stowagePlanRotationPortCalls, stowagePlan.cargoes, violations);

        return { stowagePlanRotationPortCalls, updatedStowagePlan, violations };
    };

    return {
        handleStowagePlanGuidChange,
        handleStowageCallQueryParamChanges,
        setStowageDataInStowagePlan,
    };
};
